import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import { ToastConfig } from "../../../components/Toaster";
import { Datepicker, MbscDatepickerChangeEvent } from "@mobiscroll/react";
import { createCommonUtilsApi } from "../../../api-helpers/CommonUtilsApiHelper";
import Lookups from "../../../constants/Lookups";
import { Lookup } from "common-util-experience-client";
import { AllergyCategory, AllergySeverity, AllergyStatus, AllergyType } from "../../../interfaces/allergy";
import { GetPatientAllergies, SaveAllergyPatientMapping } from "patients-experience-client";
import { createPatientApi } from "../../../api-helpers/PatientApiHelper";

interface AllergyProps{
  patientId : string,
  onSubmitAllergy: (toasterConfig: ToastConfig) => void,
  setOffCanvasShow: Dispatch<SetStateAction<boolean>>,
  selectedAllergy?: GetPatientAllergies,
  reloadGrid: () => void,
}
export const AddAllergy = (props:AllergyProps) => {
  const [validated, setValidated] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [allergyTypes, setAllergytypes] = useState<AllergyType[]>([]);
  const [allergyCategories, setAllergyCategories] = useState<AllergyCategory[]>([]);
  const [severityOfReactions, setSeverityOfReactions] = useState<AllergySeverity[]>([]);
  const [allergyStatus, setAllergyStatus] = useState<AllergyStatus[]>([]);
  const [knownSinceDatePicker, setKnownSinceDatePicker] = useState<boolean>(false);
  const [allergyForm, setAllergyForm] = useState<SaveAllergyPatientMapping>({
    allergy: "",
    typeId: "",
    categoryId: "",
    statusId: "",
    severityOfReactionId: "",
    knownSince: null,
    isDeleted: false,
  });

  const selectedAllergyId = props.selectedAllergy?.id
  const patientApi = createPatientApi()
  const apiLookup=createCommonUtilsApi()

  const getAllergyDetailsById = async () => {
    const allergyId = props.selectedAllergy && props.selectedAllergy.id!
    if (allergyId) {
      try {
        const allergyResponse= await patientApi.patientsControllerGetAllergyById(props.patientId, allergyId);
        const allergies = allergyResponse.data;
        setAllergyForm({
          allergy: allergies.allergy!,
          typeId: allergies.type.id!,
          categoryId: allergies.category.id!,
          statusId: allergies.status.id!,
          severityOfReactionId: allergies.severityOfReaction.id!,
          knownSince: allergies.knownSince,
          isDeleted: allergies.isDeleted,
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  const getAllergyTypes = async () => {
    try {
        const response= await apiLookup.lookupControllerFindAll(Lookups.ALLERGY_TYPE);
        setAllergytypes(response.data.sort((a:Lookup, b:Lookup) => a.sequence - b.sequence));
    } catch (error) {
      console.log(error);
    }
  };
  const getAllergySeverityOfReaction = async () => {
    try {
        const response= await apiLookup.lookupControllerFindAll(Lookups.SEVERITY_LEVEL);
        setSeverityOfReactions(response.data.sort((a:Lookup, b:Lookup) => a.sequence - b.sequence));
    } catch (error) {
      console.log(error);
    }
  };
  const getAllergyCategory = async () => {
    try {
        const response= await apiLookup.lookupControllerFindAll(Lookups.ALLERGY_CATEGORY);
        setAllergyCategories(response.data.sort((a:Lookup, b:Lookup) => a.sequence - b.sequence));
    } catch (error) {
      console.log(error);
    }
  };
  const getAllergyStatus = async () => {
    try {
        const response= await apiLookup.lookupControllerFindAll(Lookups.ALLERGY_STATUS);
        setAllergyStatus(response.data.sort((a:Lookup, b:Lookup) => a.sequence - b.sequence));
    } catch (error) {
      console.log(error);
    }
  };

  const handleDiscard = () => {
    props.setOffCanvasShow(false);
  };

  const dateRangeChangeHandler = (args: MbscDatepickerChangeEvent) => {
    setAllergyForm({
      ...allergyForm,
      knownSince: new Date(args.valueText!),
    });
  };

  const showResponseMessage = (response: any) => {
    let toasterConfig: ToastConfig;
    const isError = response.statusCode.toString().charAt(0) === '4' || response.statusCode.toString().charAt(0) === '5'
    if (isError) {
      toasterConfig= {
        type:'danger',
        title: `${response.message}`,
        icon: "error_outline",
      };
      props.onSubmitAllergy(toasterConfig as ToastConfig);
    } else {
      toasterConfig= {
        type:'success',
        title: `${response.message}`,
        icon: "check_circle_outline",
      };
      props.onSubmitAllergy(toasterConfig as ToastConfig);
      handleDiscard()
    }
  }

  const onChange = (event: any) => {
    const newState = {
      ...allergyForm,
      [event.target.name]: event.target.value,
    };
    setAllergyForm(newState);
  };

  const onsubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    // LoadingHelper.showLoader(".formAddAllergy");
    setIsProcessing(true)
    event.preventDefault();
    setValidated(true);

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      event.preventDefault();
      event.stopPropagation();
      setIsProcessing(false)
      return;
    }
    
    let allergyResponse;
    try {
      if(props.selectedAllergy){
        allergyResponse = await patientApi.patientsControllerUpdateAllergy(allergyForm, props.patientId, selectedAllergyId!)
      } else {
        allergyResponse = await patientApi.patientsControllerSaveAllergy(allergyForm, props.patientId)
      }
    } catch (error) {
      console.error(error);
      setIsProcessing(false)
    } finally {
      setIsProcessing(false)
      if (allergyResponse) {
        showResponseMessage(allergyResponse.data)
      }
      props.reloadGrid();
    }
  };

  useEffect(() => {
    // Checking if selected allergy is present
    if (props.selectedAllergy){
      getAllergyDetailsById()
    }
    getAllergyTypes();
    getAllergyCategory()
    getAllergySeverityOfReaction();
    getAllergyStatus();
  }, []);

  return (
    <>
      <Form
        className="d-flex flex-column h-100"
        noValidate
        validated={validated}
        onSubmit={onsubmit}
      >
        <label
  htmlFor="allergy"
  className="form-label fs-12 fw-semibold mb-1"
>
  Allergy Name
</label>
<div className="typeahead-container search-box d-flex">
  <input
    required={true}
    type="text"
    name="allergy"
    id="AllergyName"
    className="form-control fw-normal fs-12"
    placeholder="Enter Allergy Name"
    value={allergyForm.allergy}
    readOnly={props.selectedAllergy ? true : false}
    style={
      props.selectedAllergy
        ? { 
            backgroundColor: "#F0F0F0", 
            color: "#202828", 
            border: "1px solid #AEC5DC" // Blue border
          }
        : {}
    }
    onChange={(e) =>
      setAllergyForm({ ...allergyForm, allergy: e.target.value })
    }
  />
</div>

 

        <div className="flex-fill">
          {/* Type Field */}
          <div className="mb-3">
            <label htmlFor="type" className="form-label fs-12 fw-semibold mb-1">
              Type
            </label>
            <select
              className={`form-select form-select-sm ${allergyTypes.length > 0 ? '' : 'placeholder-wave'}` }
              name="typeId"
              value={allergyForm.typeId}
              onChange={(e) => { onChange(e) }}
              id="type"
              required
            >
              <option value="" disabled >Select Allergy Type</option>
              {allergyTypes.map((allergyType:AllergyType, index: number) => (
                <option key={index} value={allergyType.id}>
                  {allergyType.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div className="mb-3">
            <label
              htmlFor="categoryId"
              className="form-label fs-12 fw-semibold mb-1"
            >
              Category
            </label>
            <select
              className={`form-select form-select-sm ${allergyCategories.length > 0 ? '' : 'placeholder-wave'}` }
              name="categoryId"
              id="category"
              value={allergyForm.categoryId}
              onChange={(e) => { onChange(e) }}
              required
            >
              <option value="" disabled >Select Category</option>
              {allergyCategories.map((category:AllergyCategory, index: number) => (
                <option key={index} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

            {/* Status */}
            <div className="mb-3">
            <label
              htmlFor="statusId"
              className="form-label fs-12 fw-semibold mb-1"
            >
              Status
            </label>
            <select
              className={`form-select form-select-sm ${allergyStatus.length > 0 ? '' : 'placeholder-wave'}` }
              name="statusId"
              id="status"
              value={allergyForm.statusId}
              onChange={(e) => { onChange(e) }}
            >
              <option value="" >Select Status</option>
              {allergyStatus.map((status:AllergyStatus, index: number) => (
                <option key={index} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>

          {/* Allergenic Since and Severity */}
          <div className="row g-2 mb-3">
          <div className="col-6">
              <label
                htmlFor="severityOfReactionId"
                className="form-label fs-12 fw-semibold mb-1"
              >
                Severity of Reaction
              </label>
              <select
                className={`form-select form-select-sm ${severityOfReactions.length > 0 ? '' : 'placeholder-wave'}` }
                name="severityOfReactionId"
                id="Severity"
                value={allergyForm.severityOfReactionId}
                onChange={(e) =>
                  setAllergyForm({ ...allergyForm, severityOfReactionId: e.target.value })
                }
              >
                <option value="">
                  Select Severity of Reaction
                </option>
                {severityOfReactions.map((severity:AllergySeverity, index: number) => (
                <option key={index} value={severity.id}>
                  {severity.name}
                </option>
              ))}
              </select>
            </div>
            <div className="col-6">
              <label
                htmlFor="knownSince"
                className="form-label fs-12 fw-semibold mb-1"
              >
                Known Since
              </label>
              <div className="mobiscroll">
                <div className="mobiscroll__calendar">
                  <Datepicker
                    controls={["calendar"]}
                    value={allergyForm.knownSince}
                    placeholder="MM/DD/YYYY"
                    onChange={dateRangeChangeHandler}
                    theme="windows"
                    display="bubble"
                    touchUi={true}
                    showOnClick={false}
                    isOpen={knownSinceDatePicker}
                    onClose={() => {setKnownSinceDatePicker(false)}}
                    themeVariant="light"
                    max={new Date().toISOString()}
                    buttons={[]}
                    defaultValue={allergyForm.knownSince}
                    required
                  />
                </div>
                  <div onClick={() => setKnownSinceDatePicker(!knownSinceDatePicker)} className="mobiscroll__calendar-icon">
                      <span className="material-symbols-outlined fs-18" >
                        calendar_month
                      </span>
                  </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer with Discard and Submit Buttons */}
        <div className="offcanvas-footer text-end w-100 border-top d-flex justify-content-between border-top">
          <div>
            <button
              className="btn btn-sm btn-outline-secondary rounded-pill px-5 fs-13"
              type="button"
              onClick={handleDiscard}
            >
              Discard
            </button>
          </div>
          <div>
          <button
              className="btn btn-sm btn-secondary rounded-pill ps-4 pe-5 fs-13"
              type="submit"
              disabled = {isProcessing}
            >
              <span className="material-symbols-outlined fs-16 align-text-top">
              {isProcessing ? "" : props.selectedAllergy ? ""  : "add"}
              </span>
              {isProcessing ? "Processing..." : props.selectedAllergy ? "Update" : "Add"}
            </button>
          </div>
        </div>
      </Form>
    </>
  );
};
